import {Event} from '../constants/Event'
import {debounce} from '../utils/debounce'

/**
 * @class StateHandler
 *
 * Handles global state and makes it
 */
class StateHandler {
    /**
     * @hideconstructor
     *
     * @param {Api} api Reference to the api
     */
    constructor({api}) {
        /** @var _changed True if unsaved changes exist, false otherwise */
        this._changed = false

        /** @var _invalid Set to true if document has become invalid or broken */
        this._invalid = false

        /** @var _saving Set to true when document is being saved */
        this._saving = false

        // Take snapshot of changes once a second - if applicable
        this.addVersion = debounce(() => {
            if (this._changed && !this._invalid) {
                api.history.snapshot()
            }
        }, 1000)

        api.events.on('statehandler', Event.DOCUMENT_CHANGED, evt => {
            if (!this._invalid) {
                this.changed = true
                // Don't call addVersion if we are restoring a previous document
                if (!evt.data || (evt.data.type !== 'version' || evt.data.action !== 'update')) {
                    this.addVersion()
                }
            }
        })

        api.events.on('statehandler', Event.DOCUMENT_SAVED, () => {
            this.changed = false
            this.saving = false
        })

        api.events.on('statehandler', Event.DOCUMENT_SAVE_FAILED, () => {
            this.saving = false
        })

        api.events.on('statehandler', Event.USERACTION_CANCEL_SAVE, () => {
            this.saving = false
        })

        api.events.on('statehandler', Event.DOCUMENT_INVALIDATED, () => {
            this.invalid = true
            this.saving = false
        })
    }

    set changed(val) {
        this._changed = val
    }

    set invalid(val) {
        this._invalid = val
    }

    set saving(val) {
        this._saving = val
    }

    get changed() {
        return this._changed
    }

    get invalid() {
        return this._invalid
    }

    get saving() {
        return this._saving
    }

    /**
     * Return document part of the state object
     *
     * @return {Object}
     */
    get document() {
        const _self = this
        return Object.freeze({
            get changed() { return _self.changed },
            get invalid() { return _self.invalid },
            get saving() { return _self.saving }
        })
    }

    /**
     * Return a frozen state object representing the state of the application
     *
     * @return {Object} The frozen state object
     */
    getStateObject() {
        return Object.freeze({
            document: this.document
        })
    }
}

export {StateHandler}
